// src/pages/Travel.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import "./css/Travel.css";
import Modal from "../components/Modal";
import EditTripModal from "../components/EditTripModal";
import { useParams, useNavigate } from "react-router-dom";
import { normalizeHandle } from "../utils/handle";
import TripsList, { isTripActive, formatDateForUI, parseDateToMs, toMillis } from "../components/TripList";
import MapPanel, { extractEmbedSrc } from "../components/MapPanel";
import LiveVideoPanel from "../components/LiveVideoPanel";
import ItineraryPanel, { AddItineraryForm } from "../components/ItineraryPanel";
import GalleryPanel from "../components/GalleryPanel";
import LiveLocationPanel from '../components/LiveLocationPanel';
import SightseeingPanel from '../components/SightseeingPanel';
import { uploadFileAndSaveMeta } from "../utils/storageUploads";
import { loadTripBySlug } from "../utils/loadTripBySlug";
import ShareModal from "../components/ShareModal"; // add import
import MessageModal from "../components/MessageModal";
import InspectSightModal from "../components/InspectSightModal";
import { normalizeTripDatesCamel, toInclusiveMs } from "../utils/date";
import TripNotificationBanner from "../components/TripNotificationBanner";
import { onAuthStateChanged, getAuth } from "firebase/auth";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  deleteDoc,
  getDoc,
  getDocs,
  arrayUnion,
  arrayRemove,
  setDoc,
  serverTimestamp,
  updateDoc,
  runTransaction
} from "firebase/firestore";
import { useToast } from "../components/ToastProvider";
import { firebaseApp, auth, db, storage, isFirebaseConfigured } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

/* ---------- firebase usage flag ---------- */
const useFirebase = Boolean(isFirebaseConfigured && firebaseApp && auth && db && storage);

/* ---------- helpers ---------- */
const localUid = (prefix = "trip") =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

function normalizeDate(d) {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString().slice(0, 10);
}
function slugify(text = "") {
  return String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}
// prefer camelCase but fallback to snake_case for legacy docs
function readStart(trip) {
  if (!trip) return "";
  return trip.startDate ?? trip.start_date ?? "";
}
function readEnd(trip) {
  if (!trip) return "";
  return trip.endDate ?? trip.end_date ?? null;
}
function readStartRaw(trip) { return trip?.startDate ?? trip?.start_date ?? trip?.start ?? null; }
function readEndRaw(trip) { return trip?.endDate ?? trip?.end_date ?? trip?.end ?? null; }

// Uploads array of media items (items may have {file}) and returns the resulting array
async function uploadAndSaveMedia(items = [], tripId, user) {
  if (!Array.isArray(items) || items.length === 0) return items;
  if (!useFirebase || !user || !tripId) {
    // strip file objects and return
    return items.map((it) => {
      const copy = { ...it };
      if (copy.file) delete copy.file;
      return copy;
    });
  }

  const out = [];
  for (const it of items) {
    try {
      if (it && it.file) {
        const result = await uploadFileAndSaveMeta(it.file, tripId, user, null);
        const final = {
          id: result.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: result.name || it.name || it.file?.name || "file",
          type: result.type || (it.type || (it.file && it.file.type && it.file.type.startsWith("video") ? "video" : "image")),
          url: result.url || result.storageUrl || "",
          storagePath: result.storagePath || result.path || null,
          date: result.date || result.uploadedAt || new Date().toISOString(),
          gps: result.gps || it.gps || null,
          uploadedBy: result.uploadedBy || user?.uid || null,
          uploadedAt: result.uploadedAt || new Date().toISOString(),
          _persisted: !!result._firestorePersisted,
        };

        out.push(final);
      } else {
        const copy = { ...it };
        if (copy.file) delete copy.file;
        out.push(copy);
      }
    } catch (err) {
      console.error("uploadAndSaveMedia: upload failed for item", it?.name, err);
      const safe = { ...it };
      if (safe.file) delete safe.file;
      safe.uploaded = false;
      safe.uploadError = true;
      out.push(safe);
    }
  }
  return out;
}

// Resolve handle -> uid helpers (used by edit modal)
const resolveHandleToUidLocal = async (handleNoAt) => {
  try {
    if (!db || !handleNoAt) return null;
    const key = normalizeHandle(String(handleNoAt || ""));
    if (!key) return null;

    const hSnap = await getDoc(doc(db, "handles", key));
    if (hSnap && hSnap.exists()) {
      const d = hSnap.data();
      return d?.uid ?? d?.userId ?? d?.id ?? null;
    }

    const q = query(collection(db, "handles"), where("handle", "==", key));
    const snaps = await getDocs(q);
    if (snaps && !snaps.empty) {
      const d = snaps.docs[0].data();
      return d?.uid ?? d?.userId ?? d?.id ?? null;
    }

    const q2 = query(collection(db, "users"), where("handle", "==", key), orderBy("createdAt", "desc"));
    const snaps2 = await getDocs(q2);
    if (snaps2 && !snaps2.empty) {
      const d = snaps2.docs[0].data();
      return d?.uid ?? d?.id ?? snaps2.docs[0].id ?? null;
    }
    return null;
  } catch (err) {
    console.error("resolveHandleToUidLocal error:", err);
    return null;
  }
};

const resolveUidToHandleLocal = async (uid) => {
  try {
    if (!db || !uid) return null;
    const q = query(collection(db, "handles"), where("uid", "==", uid));
    const snaps = await getDocs(q);
    if (snaps && !snaps.empty) {
      const first = snaps.docs[0];
      if (first && first.id) return first.id;
      const d = first.data();
      return d?.handle ?? d?.name ?? null;
    }

    const uSnap = await getDoc(doc(db, "users", uid));
    if (uSnap && uSnap.exists()) {
      const data = uSnap.data();
      return data?.handle ?? data?.username ?? null;
    }

    const q2 = query(collection(db, "users"), where("uid", "==", uid));
    const snaps2 = await getDocs(q2);
    if (snaps2 && !snaps2.empty) {
      const d = snaps2.docs[0].data();
      return d?.handle ?? d?.username ?? null;
    }

    return null;
  } catch (err) {
    console.error("resolveUidToHandleLocal error:", err);
    return null;
  }
};

export default function Travel({ externalHandle = null, externalSlug = null }) {
  const navigate = useNavigate();
  const params = useParams();
  let routeHandle = (params.handle || "").toString();
  // prefer externalHandle prop (from App) if provided
  if (externalHandle) {
    routeHandle = String(externalHandle || "");
  }

  // optional slug from url or prop
  const paramSlug = (params.slug || "").toString().replace(/^\/*|\/*$/g, "");
  let routeSlug = externalSlug || paramSlug || ""; // externalSlug prop takes precedence

  const [tripTitle, setTripTitle] = useState("");
  const [tripStart, setTripStart] = useState("");
  const [tripEnd, setTripEnd] = useState("");
  const [tripPrivate, setTripPrivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [showItineraryModal, setShowItineraryModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [media, setMedia] = useState([]);
  const [savedTrips, setSavedTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [user, setUser] = useState(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(Boolean(routeHandle));
  const [isPublicView, setIsPublicView] = useState(Boolean(routeHandle));
  const [resolvedUid, setResolvedUid] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tripStartLocation, setTripStartLocation] = useState("");
  const [tripDestination, setTripDestination] = useState("");
  const [tripNotes, setTripNotes] = useState("");
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [tripToEdit, setTripToEdit] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmItin, setConfirmItin] = useState({ open: false, id: null, title: null });
  const [googleEmbedInput, setGoogleEmbedInput] = useState("");
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [savedSights, setSavedSights] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareFollowers, setShareFollowers] = useState([]);
  const [shareModalContext, setShareModalContext] = useState({ isProfile: true, handle: "", tripName: "" });
  const [position, setPosition] = useState(null);
  const watchIdRef = useRef(null);
  const likeInFlightRef = useRef(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [sightModalOpen, setSightModalOpen] = useState(false);
  const [modalSight, setModalSight] = useState(null);
  const { addToast } = useToast();

  // push local toast to global provider whenever it changes
  useEffect(() => {
    if (!toast) return;
    // the provider expects { msg, type } like your code already uses
    addToast({ msg: toast.msg, type: toast.type }, 3500);
    // we don't clear local toast here so existing codepaths that set it to null still work
  }, [toast, addToast]);


  // load trip by slug from Firestore and apply visibility checks (this is your original flow)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const slug = (routeSlug || "").toString().trim();
        if (!slug) return;
        console.log("[Travel] loading trip by slug:", slug);
        if (!useFirebase || !db) {
          console.warn("[Travel] Firebase not configured; cannot load trip by slug");
          return;
        }

        // Wait until resolvedUid exists (owner uid for the handle),
        // but don't block forever — if handleless slug, we still attempt load
        // (in case you have slug-only URLs that map globally).
        // So only block if route uses handle-based pages and resolvedUid is required.
        // If your UX requires resolvedUid before a slug lookup, keep the earlier guard.
        // For now: try to load slug mapping immediately.
        try {
          // Use the safe helper (slugs/{slug} -> trips/{tripId})
          const tripData = await loadTripBySlug(slug);
          if (cancelled) return;

          // tripData returned by loadTripBySlug should include id/data or throw
          const baseTrip = {
            ...tripData,
            trip_id: tripData.id || tripData.trip_id || tripData.tripId || `trip_local_${Date.now()}`,
            title: tripData.title || tripData.name || tripData.slug || "Untitled Trip",
          };

          // normalizeTripDatesCamel should produce e.g. startDate / endDate (camel)
          // canonical trip object (use this variable everywhere below)
          const normalized = normalizeTripDatesCamel(baseTrip) || { ...baseTrip };

          const startDateCamel = normalized.startDate ?? normalized.start_date ?? normalized.start ?? null;
          const endDateCamel = normalized.endDate ?? normalized.end_date ?? normalized.end ?? null;

          const trip = {
            ...normalized,
            // canonical camelCase
            startDate: startDateCamel,
            endDate: endDateCamel,
            // maintain snake_case for legacy code that still reads it
            start_date: startDateCamel ? (typeof startDateCamel === "string" ? startDateCamel : new Date(startDateCamel).toISOString().slice(0, 10)) : "",
            end_date: endDateCamel ? (typeof endDateCamel === "string" ? endDateCamel : new Date(endDateCamel).toISOString().slice(0, 10)) : null,

            private: (tripData.visibility === "private") || !!tripData.private,
            itinerary: tripData.itinerary || normalized.itinerary || [],
            media: tripData.media || normalized.media || [],
            last_position: tripData.last_position || normalized.last_position || normalized.lastPosition || null,
            createdAt: tripData.createdAt ?? tripData.created_at ?? normalized.createdAt ?? normalized.created_at ?? new Date().toISOString(),
            created_at: tripData.created_at ?? tripData.createdAt ?? normalized.created_at ?? normalized.createdAt ?? new Date().toISOString(),
            ownerId: tripData.owner_id || tripData.ownerId || normalized.ownerId || null,
            visibility: tripData.visibility || normalized.visibility || (tripData.private ? "private" : "public"),
            allowedUsers: Array.isArray(tripData.allowedUsers) ? tripData.allowedUsers : (Array.isArray(normalized.allowedUsers) ? normalized.allowedUsers : []),
            ...tripData,
          };



          // Apply visibility checks on client for UX (server rules enforce real security)
          const ownerId = trip.ownerId;
          const allowedUsers = trip.allowedUsers || [];
          const isOwnerLocal = user && ownerId && user.uid === ownerId;
          const isAllowed = user && Array.isArray(allowedUsers) && allowedUsers.includes(user.uid);

          if (normalized.visibility === "private" && !isOwnerLocal && !isAllowed) {
            if (!cancelled) {
              setNotFound(true);
              setToast({ msg: "This trip is private", type: "warning" });
              setTimeout(() => setToast(null), 2000);
            }
            return;
          }

          if (!cancelled) {
            setSelectedTripId(trip.trip_id);
            setSelectedTrip(trip);
            setTripTitle(trip.title || "");
            setTripStart(readStart(trip));
            setTripEnd(trip.endDate ?? trip.end_date ?? "");
            setTripPrivate(!!trip.private);
            setItinerary(Array.isArray(trip.itinerary) ? trip.itinerary : []);
            setTripStartLocation(trip.startLocation || trip.start_location || "");
            setTripDestination(trip.destination || trip.dest || "");
            setTripNotes(trip.notes || "");
            setMedia(
              (trip.media || []).map((x) => ({
                id: x.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                type: x.type || "image",
                url: x.storageUrl || x.url || "",
                name: x.name || "",
                date: x.date || new Date().toISOString(),
              }))
            );
            // increment view for slug-loaded trip (deduped by incrementViewOnce)
            try { incrementViewOnce(trip.trip_id); } catch (e) { console.warn("incrementViewOnce error (slug loader)", e); }

            if (trip.last_position) setPosition(trip.last_position);
            // canonical slug navigation:
            const handlePart = normalizeHandle(profile?.handle || routeHandle || "");
            const basePath = handlePart ? `/@${handlePart}` : "/Travel";
            const slugToUse = trip.slug || slugify(trip.title || "") || trip.trip_id;
            navigate(`${basePath}/${slugToUse}`, { replace: true });
          }
        } catch (err) {
          // loadTripBySlug will throw on missing slug or permission-denied on trip get
          console.error("[Travel] loadTripBySlug error:", err);
          if (cancelled) return;

          // Prefer to show "not found" for missing slug, but distinguish permission errors to help debugging
          const code = err?.code || "";
          const msg = (err && err.message) || String(err);

          if (code === "permission-denied" || msg.toLowerCase().includes("permission") || msg.toLowerCase().includes("denied")) {
            // Firestore rejected the trip get — likely private/restricted or rules issue
            setNotFound(true);
            setToast({ msg: "Access denied to this trip (private or insufficient permissions)", type: "warning" });
            setTimeout(() => setToast(null), 3000);
          } else {
            // fallback: slug missing or other error
            setNotFound(true);
          }
        }
      } catch (err) {
        console.error("[Travel] unexpected slug loader error:", err);
        if (!cancelled) setNotFound(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeSlug, user, profile, useFirebase, db, navigate, routeHandle]);

  // followers and follow state snapshots
  useEffect(() => {
    if (!useFirebase || !db || !user || !resolvedUid) return;
    const followDoc = doc(db, "follows", `${user.uid}_${resolvedUid}`);
    const unsub = onSnapshot(followDoc, (snap) => {
      setIsFollowing(snap.exists());
    }, (err) => console.error("follow status snapshot failed", err));
    return () => unsub && unsub();
  }, [db, user, resolvedUid]);

  useEffect(() => {
    if (!useFirebase || !db || !resolvedUid) return;
    const q = query(collection(db, "follows"), where("followeeId", "==", resolvedUid));
    const unsub = onSnapshot(q, (snap) => {
      setFollowerCount(snap.size);
    }, (err) => console.error("followers count snapshot failed", err));
    return () => unsub && unsub();
  }, [db, resolvedUid]);

  const isOwner = Boolean(user && selectedTrip && (user.uid === selectedTrip.ownerId || user.uid === selectedTrip.owner_id));

  async function saveGoogleEmbedForSelectedTrip() {
    if (!selectedTrip || !selectedTrip.trip_id) {
      setToast({ msg: "Select a trip first", type: "warning" });
      setTimeout(() => setToast(null), 2000);
      return;
    }
    const src = extractEmbedSrc(googleEmbedInput || selectedTrip.googleEmbed || "");
    if (!src) {
      setToast({ msg: "Please paste an iframe or embed URL", type: "warning" });
      setTimeout(() => setToast(null), 2200);
      return;
    }

    const payload = { trip_id: selectedTrip.trip_id, googleEmbed: src };
    applyLocalEdit(payload);
    setSelectedTrip((s) => s ? { ...s, googleEmbed: src } : s);
    setToast({ msg: "Map embed applied", type: "success" });
    setTimeout(() => setToast(null), 1400);

    const ownerFlag = user && (selectedTrip.ownerId === user.uid || selectedTrip.owner_id === user.uid);
    if (useFirebase && db && user && ownerFlag) {
      try {
        const dr = doc(db, "trips", selectedTrip.trip_id);
        await updateDoc(dr, { googleEmbed: src, updatedAt: new Date().toISOString() });
        setToast({ msg: "Map embed saved to trip", type: "success" });
        setTimeout(() => setToast(null), 1400);
        return;
      } catch (err) {
        console.error("save google embed to firestore failed", err);
        setToast({ msg: "Saved locally (failed to persist)", type: "warning" });
        setTimeout(() => setToast(null), 2000);
      }
    } else {
      setToast({ msg: "Embed stored locally (not persisted)", type: "info" });
      setTimeout(() => setToast(null), 1400);
    }
  }

  // ---------- handle -> uid resolution (robust/fault-tolerant) ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setProfileLoading(true);
      setNotFound(false);
      setResolvedUid(null);
      setProfile(null);
      try {
        if (!routeHandle) {
          setIsPublicView(false);
          if (!cancelled) setProfileLoading(false);
          return;
        }

        // Defensive: try normalizeHandle, but use a tolerant fallback if it returns null
        let normalized = null;
        try {
          normalized = normalizeHandle(routeHandle);
        } catch (e) {
          console.warn("[Travel] normalizeHandle threw:", e);
          normalized = null;
        }

        // Fallback normalization for common malformed inputs (%40, leading slashes, stray chars)
        if (!normalized && routeHandle) {
          let alt = String(routeHandle || "");
          try { alt = decodeURIComponent(alt); } catch (e) { /* ignore decode errors */ }
          alt = alt.trim().replace(/^\/+/, "").replace(/^@+/, "");
          // remove non-alphanum except . _ - and collapse spaces
          alt = alt.replace(/[^a-zA-Z0-9\s._-]/g, "").replace(/\s+/g, "-").toLowerCase();
          alt = alt.replace(/^[\-_\.]+|[\-_\.]+$/g, "").slice(0, 64);
          try {
            normalized = normalizeHandle(alt);
          } catch (e) {
            normalized = null;
          }
          console.debug("[Travel] fallback-normalized routeHandle ->", { routeHandle, alt, normalized });
        }

        if (!normalized) {
          // don't set notFound immediately if firebase not configured (local dev)
          if (!useFirebase) {
            setIsPublicView(false);
            if (!cancelled) setProfileLoading(false);
            return;
          }

          console.warn("[Travel] invalid handle after normalize/fallback:", routeHandle);
          setNotFound(true);
          if (!cancelled) setProfileLoading(false);
          return;
        }

        if (!useFirebase) {
          setToast({ msg: "Firebase not configured — showing local demo view", type: "warning" });
          setTimeout(() => setToast(null), 2200);
          setIsPublicView(false);
          if (!cancelled) setProfileLoading(false);
          return;
        }

        try {
          const handleRef = doc(db, "handles", normalized);
          const handleSnap = await getDoc(handleRef);
          if (cancelled) return;

          if (handleSnap && handleSnap.exists()) {
            const data = handleSnap.data();
            const uid = data?.uid ?? null;
            if (!uid) {
              setNotFound(true);
              return;
            }
            setResolvedUid(uid);
            return;
          }

          // fallback: query users collection for handle field
          const usersCol = collection(db, "users");
          const q = query(usersCol, where("handle", "==", normalized), orderBy("createdAt", "desc"));
          const snaps = await getDocs(q);
          if (cancelled) return;

          if (!snaps || snaps.empty) {
            setNotFound(true);
            return;
          }

          const docSnap = snaps.docs[0];
          setResolvedUid(docSnap.id);
          return;
        } catch (err) {
          console.error("[Travel] error resolving handle from Firestore:", err);
          setToast({ msg: "Error fetching profile — showing local view", type: "warning" });
          setTimeout(() => setToast(null), 2000);
          setIsPublicView(false);
          return;
        }
      } catch (err) {
        console.error("[Travel] unexpected error in handle resolver:", err);
        setNotFound(true);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeHandle, useFirebase]);

  /* ---------- load profile by uid ---------- */
  useEffect(() => {
    if (!resolvedUid) return;
    let cancelled = false;
    (async () => {
      setProfileLoading(true);
      try {
        const uref = doc(db, "users", resolvedUid);
        const snap = await getDoc(uref);
        if (cancelled) return;
        if (snap && snap.exists()) {
          setProfile(snap.data());
          setIsPublicView(true);
          setNotFound(false);
        } else {
          setProfile(null);
          setNotFound(true);
        }
      } catch (err) {
        console.error("[Travel] failed to load profile:", err);
        setProfile(null);
        setNotFound(true);
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedUid]);

  /* ---------- trips subscription & auth handling ---------- */
  useEffect(() => {
    if (!useFirebase) {
      setFirebaseReady(false);
      return; // local only
    }

    let cancelled = false;
    let tripsUnsub = null;
    let authUnsub = null;
    const tripsMap = new Map();

    const processSnapshot = (snapshot) => {
      return snapshot.docs.map((d) => {
        const data = d.data() || {};
        const base = { trip_id: d.id, ...data };
        const withDates = normalizeTripDatesCamel(base);
        return {
          ...withDates,
          title: (withDates.title && String(withDates.title).trim()) || null || "Untitled Trip",
          private: (data.visibility === "private") || !!data.private,
          visibility: data.visibility || (data.private ? "private" : "public"),
          itinerary: data.itinerary || [],
          media: data.media || [],
          last_position: data.last_position || data.lastPosition || null,
          slug: data.slug || null,
          created_at:
            (data.createdAt && typeof data.createdAt.toDate === "function")
              ? data.createdAt.toDate().toISOString()
              : (data.createdAt || data.created_at || null),
          updated_at: data.updatedAt || data.updated_at || null,
          ownerId: data.owner_id || data.ownerId || null,
          allowedUsers: Array.isArray(data.allowedUsers) ? data.allowedUsers : [],
        };
      });
    };

    const pushSnapshotToMap = (items) => {
      items.forEach((t) => tripsMap.set(t.trip_id, t));
      const arr = Array.from(tripsMap.values()).sort((a, b) => {
        const ta = a.created_at ? Date.parse(a.created_at) : 0;
        const tb = b.created_at ? Date.parse(b.created_at) : 0;
        return tb - ta;
      });

      setSavedTrips(arr);

      if (routeSlug) {
        try {
          const bySlug = arr.find(
            (t) =>
              (t.slug && t.slug === routeSlug) ||
              (t.slug && slugify(t.slug) === routeSlug) ||
              (t.title && slugify(t.title) === routeSlug) ||
              t.trip_id === routeSlug
          );
          if (bySlug) {
            setSelectedTripId(bySlug.trip_id);
            setSelectedTrip(bySlug);
            setTripTitle(bySlug.title || "");
            setTripStart(bySlug.startDate ?? bySlug.start_date ?? "");
            setTripEnd(bySlug.endDate ?? bySlug.end_date ?? "");
            setTripPrivate(!!bySlug.private);
            setItinerary(bySlug.itinerary || []);
            setTripStartLocation(bySlug.startLocation || bySlug.start_location || "");
            setTripDestination(bySlug.destination || bySlug.dest || "");
            setTripNotes(bySlug.notes || "");
            setMedia(
              (bySlug.media || []).map((x) => ({
                id: x.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                type: x.type || "image",
                url: x.storageUrl || x.url || "",
                name: x.name || "",
                date: x.date || new Date().toISOString(),
              }))
            );
            if (bySlug.last_position) setPosition(bySlug.last_position);
            // count view for trips auto-selected by slug
            try { incrementViewOnce(bySlug.trip_id); } catch (e) { console.warn("incrementViewOnce error (pushSnapshot bySlug)", e); }

          }
        } catch (err) {
          console.warn("[Travel] routeSlug auto-select failed:", err);
        }
      }

      try {
        if (selectedTripId) {
          const updated = tripsMap.get(selectedTripId);
          if (updated) {
            const merged = { ...(selectedTrip || {}), ...updated };
            setSelectedTrip(merged);

            setTripTitle(merged.title || "");
            setTripStart(merged.startDate ?? merged.start_date ?? "");
            setTripEnd(merged.endDate ?? merged.end_date ?? "");
            setTripPrivate(!!merged.private);
            setItinerary(Array.isArray(merged.itinerary) ? merged.itinerary : (itinerary || []));
            setTripStartLocation(merged.startLocation || merged.start_location || "");
            setTripDestination(merged.destination || merged.dest || "");
            setTripNotes(merged.notes || merged.notes || "");
            setMedia(
              (merged.media || []).map((x) => ({
                id: x.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                type: x.type || "image",
                url: x.storageUrl || x.url || "",
                name: x.name || "",
                date: x.date || new Date().toISOString(),
              }))
            );

            if (merged.last_position) setPosition(merged.last_position);
          } else {
            setSelectedTripId(null);
            setSelectedTrip(null);
          }
        }
      } catch (err) {
        console.warn("[Travel] pushSnapshotToMap: failed to sync selectedTrip", err);
      }

      try {
        if (!selectedTripId && arr.length > 0) {
          const now = Date.now();

          const candidates = arr
            .map((t) => {
              const startedFlag = !!t.started;
              const startedAtNum = t.startedAt ? Date.parse(t.startedAt) : (t.started_at ? Date.parse(t.started_at) : NaN);
              return { t, startedFlag, startedAtNum: Number.isFinite(startedAtNum) ? startedAtNum : (startedFlag ? 0 : NaN) };
            })
            .filter((x) => x.startedFlag || (Number.isFinite(x.startedAtNum) && x.startedAtNum <= now));

          if (candidates.length > 0) {
            candidates.sort((a, b) => (b.startedAtNum || 0) - (a.startedAtNum || 0));
            const chosen = candidates[0].t;

            setSelectedTripId(chosen.trip_id);
            setSelectedTrip(chosen);
            setTripTitle(chosen.title || "");
            setTripStart(chosen.startDate ?? chosen.start_date ?? "");
            setTripEnd(chosen.endDate ?? chosen.end_date ?? "");
            setTripPrivate(!!chosen.private);
            setItinerary(chosen.itinerary || []);
            setTripStartLocation(chosen.startLocation || chosen.start_location || "");
            setTripDestination(chosen.destination || chosen.dest || "");
            setTripNotes(chosen.notes || "");
            setMedia(
              (chosen.media || []).map((x) => ({
                id: x.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                type: x.type || "image",
                url: x.storageUrl || x.url || "",
                name: x.name || "",
                date: x.date || new Date().toISOString(),
              }))
            );
            if (chosen.last_position) setPosition(chosen.last_position);
            // count view for the auto-selected started trip
            try { incrementViewOnce(chosen.trip_id); } catch (e) { console.warn("incrementViewOnce error (auto-select chosen)", e); }

          }
        }
      } catch (err) {
        console.warn("[Travel] auto-select started trip failed:", err);
      }
    };

    const setupSubscriptions = (currentUser) => {
      if (tripsUnsub) {
        try { tripsUnsub(); } catch (e) { }
      }
      tripsMap.clear();

      const unsubscribers = [];
      const ownerId = resolvedUid || (currentUser && currentUser.uid) || null;
      if (!ownerId) {
        setSavedTrips([]);
        tripsUnsub = null;
        return;
      }

      const isViewerOwner = currentUser && currentUser.uid === ownerId;

      if (isViewerOwner) {
        try {
          const qOwnerAll = query(
            collection(db, "trips"),
            where("owner_id", "==", ownerId),
            orderBy("createdAt", "desc")
          );

          const unsubOwnerAll = onSnapshot(
            qOwnerAll,
            (snap) => {
              try {
                const items = processSnapshot(snap);
                pushSnapshotToMap(items);
              } catch (err) {
                console.error("[Travel.qOwnerAll snapshot] processing error:", err);
              }
            },
            (err) => {
              if (err?.code === "permission-denied") {
                console.warn(`[Travel] Permission denied while subscribing to owner-all trips for ownerId=${ownerId}.`);
                return;
              }
              console.error("[Travel] owner-all trips subscription failed:", err);
              setToast({ msg: "Failed to sync trips (owner-all)", type: "warning" });
              setTimeout(() => setToast(null), 2000);
            }
          );

          unsubscribers.push(unsubOwnerAll);
        } catch (err) {
          console.error("Failed to subscribe to owner-all trips", err);
        }
      }

      if (!isViewerOwner) {
        try {
          const qPublic = query(
            collection(db, "trips"),
            where("owner_id", "==", ownerId),
            where("visibility", "==", "public"),
            orderBy("createdAt", "desc")
          );

          const unsubPublic = onSnapshot(
            qPublic,
            (snap) => {
              try {
                const items = processSnapshot(snap);
                pushSnapshotToMap(items);
              } catch (err) {
                console.error("[Travel.qPublic snapshot] processing error:", err);
              }
            },
            (err) => {
              if (err?.code === "permission-denied") {
                console.warn(`[Travel] Permission denied while subscribing to public trips for ownerId=${ownerId}.`);
                return;
              }
              console.error("[Travel] public trips subscription failed:", err);
              setToast({ msg: "Failed to sync public trips", type: "warning" });
              setTimeout(() => setToast(null), 2000);
            }
          );

          unsubscribers.push(unsubPublic);
        } catch (err) {
          console.error("Failed to subscribe to public trips", err);
        }
      }

      if (!(currentUser && currentUser.uid === ownerId) && currentUser && currentUser.uid) {
        try {
          const qAllowed = query(
            collection(db, "trips"),
            where("owner_id", "==", ownerId),
            where("allowedUsers", "array-contains", currentUser.uid),
            orderBy("createdAt", "desc")
          );

          const unsubAllowed = onSnapshot(
            qAllowed,
            (snap) => {
              try {
                const items = processSnapshot(snap);
                pushSnapshotToMap(items);
              } catch (err) {
                console.error("[Travel.qAllowed snapshot] processing error:", err);
              }
            },
            (err) => {
              if (err?.code === "permission-denied") {
                console.warn(`[Travel] Permission denied while subscribing to allowed trips for ownerId=${ownerId} (viewer=${currentUser?.uid}).`);
                return;
              }
              console.error("allowed trips onSnapshot error", err);
              setToast({ msg: "Failed to sync allowed trips", type: "warning" });
              setTimeout(() => setToast(null), 2000);
            }
          );

          unsubscribers.push(unsubAllowed);
        } catch (err) {
          console.error("Failed to subscribe to allowed trips", err);
        }
      }

      tripsUnsub = () => {
        try {
          unsubscribers.forEach((u) => { try { u && u(); } catch (e) { } });
        } catch (e) { }
      };
    };

    (async () => {
      try {
        authUnsub = onAuthStateChanged(auth, (u) => {
          setUser(u || null);
          setFirebaseReady(true);
          setupSubscriptions(u);
        });
      } catch (err) {
        console.error("[Travel] Firebase init error", err);
        setFirebaseReady(false);
      }
    })();

    return () => {
      try { cancelled = true; } catch (e) { }
      try { authUnsub && authUnsub(); } catch (e) { }
      try { tripsUnsub && tripsUnsub(); } catch (e) { }
    };
  }, [resolvedUid]);

  // trip:created handler (for local-created trips or events)
  useEffect(() => {
    let mounted = true;

    const handleLoadedTripFromDoc = (docData) => {
      if (!mounted || !docData) return;
      const base = { trip_id: docData.trip_id || docData.id || `trip_local_${Date.now()}`, ...docData };
      const normalized = normalizeTripDatesCamel(base);
      normalized.title = normalized.title || "Untitled Trip";
      normalized.private = (docData.visibility === "private") || !!docData.private;
      normalized.itinerary = docData.itinerary || [];
      normalized.media = docData.media || [];
      normalized.last_position = docData.last_position || docData.lastPosition || null;
      normalized.created_at = docData.created_at || new Date().toISOString();
      normalized.ownerId = docData.owner_id || docData.ownerId || null;
      normalized.visibility = docData.visibility || (docData.private ? "private" : "public");
      normalized.allowedUsers = Array.isArray(docData.allowedUsers) ? docData.allowedUsers : (docData.allowed_users || []);

      setSavedTrips((prev) => {
        if (prev.some((t) => t.trip_id === normalized.trip_id)) return prev;
        return [normalized, ...prev];
      });

      setSelectedTripId(normalized.trip_id);
      setSelectedTrip(normalized);
      setTripTitle(normalized.title || "");
      setTripPrivate(!!normalized.private);
      setItinerary(normalized.itinerary || []);
      setTripStartLocation(normalized.startLocation || "");
      setTripDestination(normalized.destination || "");
      setTripNotes(normalized.notes || "");
      setTripStart(normalized.startDate || "");
      setTripEnd(normalized.endDate || "");
      setMedia(
        (normalized.media || []).map((x) => ({
          id: x.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: x.type || "image",
          url: x.storageUrl || x.url || "",
          name: x.name || "",
          date: x.date || new Date().toISOString(),
        }))
      );
      if (normalized.last_position) setPosition(normalized.last_position);
      // increment view when a trip is loaded/created and auto-selected
      try { incrementViewOnce(normalized.trip_id); } catch (e) { console.warn("incrementViewOnce error (trip:created)", e); }
      setToast({ msg: "Trip added and loaded", type: "success" });
      setTimeout(() => setToast(null), 2000);
    };

    const onTripCreated = async (e) => {
      try {
        const detail = e?.detail || {};
        if (detail.trip) {
          handleLoadedTripFromDoc(detail.trip);
          return;
        }

        const tripId = detail.trip_id || detail.id || null;
        const ownerHint = detail.owner_id || detail.ownerId || null;
        const visibilityHint = detail.visibility || null;

        let docData = null;

        if (useFirebase && user && tripId) {
          if (ownerHint && user.uid === ownerHint) {
            try {
              const dr = doc(db, "trips", tripId);
              const snap = await getDoc(dr);
              if (snap && snap.exists()) {
                docData = { trip_id: snap.id, ...snap.data() };
              }
            } catch (err) {
              console.error("fetch created trip error (owner)", err);
            }
          } else if (visibilityHint === "public") {
            try {
              const dr = doc(db, "trips", tripId);
              const snap = await getDoc(dr);
              if (snap && snap.exists()) {
                docData = { trip_id: snap.id, ...snap.data() };
              }
            } catch (err) {
              console.error("fetch created trip error (public)", err);
            }
          } else if (user && user.uid && ownerHint && ownerHint !== user.uid) {
            try {
              const dr = doc(db, "trips", tripId);
              const snap = await getDoc(dr);
              if (snap && snap.exists()) {
                docData = { trip_id: snap.id, ...snap.data() };
              }
            } catch (err) {
              console.warn("fetch created trip blocked by rules or not allowed:", err?.message || err);
            }
          }
        }

        if (!docData) {
          const fallbackRaw = {
            trip_id: tripId || localUid(),
            title: detail.title || detail.name || "Untitled Trip",
            startDate: detail.startDate ?? detail.start_date ?? detail.start ?? null,
            endDate: detail.endDate ?? detail.end_date ?? detail.end ?? null,
            itinerary: detail.itinerary || [],
            media: detail.media || [],
            last_position: detail.last_position || null,
            created_at: detail.created_at || new Date().toISOString(),
            owner_id: ownerHint,
            visibility: visibilityHint || "private",
            allowedUsers: detail.allowedUsers || [],
            ...detail,
          };
          const fallback = normalizeTripDatesCamel(fallbackRaw);
          handleLoadedTripFromDoc(fallback);
          return;
        }

        handleLoadedTripFromDoc(docData);
      } catch (err) {
        console.error("Error handling trip:created:", err);
        setToast({ msg: "Trip added (partial)", type: "warning" });
        setTimeout(() => setToast(null), 2000);
      }
    };

    window.addEventListener("trip:created", onTripCreated);
    return () => {
      mounted = false;
      window.removeEventListener("trip:created", onTripCreated);
    };
  }, [user]);

  // Add this useEffect inside the Travel component (e.g. right after the trip:created effect)
  useEffect(() => {
    const onTripDeleted = (e) => {
      try {
        const detail = e?.detail || {};
        // Support several possible identifiers sent by the deleter
        const deletedId = detail.trip_id || detail.id || null;
        const deletedSlug = detail.slug || null;
        const deletedDocId = detail.docId || null; // optional

        // If no identifier provided, bail
        if (!deletedId && !deletedSlug && !deletedDocId) return;

        // Remove from savedTrips
        setSavedTrips((prev) => {
          if (!Array.isArray(prev) || prev.length === 0) return prev;
          return prev.filter((t) => {
            const tid = t.trip_id || t.id || t._id || t.slug || "";
            // match by id or slug
            if (deletedId && tid === deletedId) return false;
            if (deletedDocId && (t.trip_id === deletedDocId || t.id === deletedDocId)) return false;
            if (deletedSlug && (String(t.slug) === String(deletedSlug) || slugify(String(t.title || "")).toString() === String(deletedSlug))) return false;
            return true;
          });
        });

        // If the deleted trip is currently selected, clear selection and navigate away
        setSelectedTripId((currentSelectedId) => {
          const match = currentSelectedId === deletedId || currentSelectedId === deletedDocId;
          if (match) {
            // clear selectedTrip state
            setSelectedTrip(null);
            // navigate to owner's base path (profile or home)
            try {
              const handlePart = normalizeHandle(profile?.handle || routeHandle || "");
              const basePath = handlePart ? `/@${handlePart}` : "/";
              navigate(basePath, { replace: true });
            } catch (err) {
              // ignore navigation errors
            }
            return null;
          }
          return currentSelectedId;
        });

      } catch (err) {
        console.error("onTripDeleted handler failed:", err);
      }
    };

    window.addEventListener("trip:deleted", onTripDeleted);
    return () => window.removeEventListener("trip:deleted", onTripDeleted);
  }, [navigate, profile, routeHandle]);

  // saved sights listener
  useEffect(() => {
    if (!useFirebase || !db || !selectedTrip?.trip_id) {
      setSavedSights([]);
      return;
    }

    const tripId = selectedTrip.trip_id;
    let q;
    try {
      q = query(collection(db, "trips", tripId, "sights"), orderBy("createdAt", "desc"));
    } catch (err) {
      q = collection(db, "trips", tripId, "sights");
    }

    const unsub = onSnapshot(
      q,
      (snap) => {
        try {
          const arr = [];
          snap.forEach((d) => {
            const data = d.data() || {};
            arr.push({
              id: d.id,
              provider: data.provider || "unknown",
              placeId: data.placeId || data.place_id || "",
              name: data.name || "",
              category: data.category || "",
              location: data.location || {},
              photoUrl: data.photoUrl || "",
              mode: data.mode || "",
              raw: data,
            });
          });
          setSavedSights(arr);
        } catch (err) {
          console.error("[Travel] sights snapshot processing failed:", err);
          setSavedSights([]);
        }
      },
      (err) => {
        console.error("[Travel] sights onSnapshot failed:", err);
      }
    );

    return () => {
      try { unsub && unsub(); } catch (e) { }
    };
  }, [db, selectedTrip?.trip_id, useFirebase]);

  // Basic trip actions (stop, extend)
  async function stopTripNow() {
    if (!selectedTrip || !selectedTrip.trip_id) {
      setToast({ msg: "No trip selected", type: "warning" }); setTimeout(() => setToast(null), 1400); return;
    }
    const isOwnerLocal = user && (selectedTrip.ownerId === user.uid || selectedTrip.owner_id === user.uid);
    if (!isOwnerLocal) {
      setToast({ msg: "Only the owner can stop this trip", type: "warning" }); setTimeout(() => setToast(null), 1400); return;
    }

    const payload = { trip_id: selectedTrip.trip_id, stopped: true, stoppedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    applyLocalEdit(payload);
    setSelectedTrip((s) => (s ? { ...s, ...payload } : s));
    setToast({ msg: "Stopping trip...", type: "info" });

    if (useFirebase && db && user) {
      try {
        const dr = doc(db, "trips", selectedTrip.trip_id);
        await updateDoc(dr, { stopped: true, stoppedAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
        setToast({ msg: "Trip stopped", type: "success" });
      } catch (err) {
        console.error("stopTripNow: firestore update failed", err);
        setToast({ msg: "Stopped locally (failed to persist)", type: "warning" });
      }
    } else {
      setToast({ msg: "Stopped locally", type: "info" });
    }
    setTimeout(() => setToast(null), 1600);
  }
  // Example helper: parseDateToMsInclusive
  function parseDateToMsInclusive(input) {
    return toInclusiveMs(input);
  }

  async function extendEndBy24Hours() {
    if (!selectedTrip || !selectedTrip.trip_id) {
      setToast({ msg: "No trip selected", type: "warning" });
      setTimeout(() => setToast(null), 1400);
      return;
    }
    const isOwnerLocal = user && (selectedTrip.ownerId === user.uid || selectedTrip.owner_id === user.uid);
    if (!isOwnerLocal) {
      setToast({ msg: "Only the owner can modify this trip", type: "warning" });
      setTimeout(() => setToast(null), 1400);
      return;
    }

    const endRaw = selectedTrip.endDate ?? selectedTrip.end_date ?? selectedTrip.end_at ?? selectedTrip.endAt;
    let endMs = parseDateToMsInclusive(endRaw);
    if (!Number.isFinite(endMs)) {
      endMs = Date.now();
    }

    const newEndMs = endMs + 24 * 60 * 60 * 1000;
    // produce a LOCAL YYYY-MM-DD (so stored date means local date)
    const d = new Date(newEndMs);
    const newEndIsoLocal =
      d.getFullYear() +
      "-" +
      String(d.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(d.getDate()).padStart(2, "0");

    const payload = {
      trip_id: selectedTrip.trip_id,
      endDate: newEndIsoLocal,
      updatedAt: new Date().toISOString(),
    };

    // apply locally first for snappy UX
    applyLocalEdit(payload);
    setSelectedTrip((s) => (s ? { ...s, ...payload } : s));
    setToast({ msg: "End date extended by 24 hours", type: "success" });

    if (useFirebase && db && user) {
      try {
        const dr = doc(db, "trips", selectedTrip.trip_id);
        await updateDoc(dr, { endDate: newEndIsoLocal, updatedAt: new Date().toISOString() });
        setToast({ msg: "End date updated", type: "success" });
      } catch (err) {
        console.error("extendEndBy24Hours: firestore update failed", err);
        setToast({ msg: "Extended locally (failed to persist)", type: "warning" });
      }
    } else {
      setToast({ msg: "Extended locally", type: "info" });
    }

    setTimeout(() => setToast(null), 1600);
  }

  const createLocalTrip = (overrides = {}) => {
    const title = overrides.title || "New Trip";
    const computedSlug = overrides.slug || slugify(title) || localUid("slug");
    const detail = {
      trip_id: localUid(),
      title,
      slug: computedSlug,
      startDate: normalizeDate(new Date()),
      endDate: null,
      itinerary: [],
      media: [],
      last_position: null,
      created_at: new Date().toISOString(),
      ownerId: (user && user.uid) || overrides.ownerId || overrides.owner_id || null,
      owner_id: (user && user.uid) || overrides.ownerId || overrides.owner_id || null,
      ...overrides,
    };
    window.dispatchEvent(new CustomEvent("trip:created", { detail }));

    try {
      const handlePart = normalizeHandle(profile?.handle || routeHandle || "");
      const basePath = handlePart ? `/@${handlePart}` : "/Travel";
      navigate(`${basePath}/${detail.slug}`, { replace: true });
    } catch (err) { }
  };

  // save itinerary helper
  async function saveItineraryForTrip(tripId, newItinerary) {
    if (!useFirebase || !db) {
      return { ok: false, reason: "no-firebase" };
    }
    if (!tripId) return { ok: false, reason: "no-trip-id" };
    try {
      const docRef = doc(db, "trips", tripId);
      await updateDoc(docRef, { itinerary: newItinerary, updatedAt: new Date().toISOString() });
      return { ok: true };
    } catch (err) {
      console.error("[Travel] saveItineraryForTrip failed:", err);
      return { ok: false, reason: err?.message || String(err) };
    }
  }

  const confirmDeleteItinerary = (itemId, title = "") => {
    setConfirmItin({ open: true, id: itemId, title });
  };

  const addItinerary = async (item) => {
    setItinerary((prev) => {
      const arr = [...(Array.isArray(prev) ? prev : [])];
      arr.push(item);
      return arr;
    });

    const tripId = selectedTrip?.trip_id || selectedTripId;
    const isOwnerLocal = user && (selectedTrip?.ownerId === user.uid || selectedTrip?.owner_id === user.uid);
    if (useFirebase && db && tripId && isOwnerLocal) {
      try {
        const base = Array.isArray(selectedTrip?.itinerary) ? [...selectedTrip.itinerary] : Array.isArray(itinerary) ? [...itinerary] : [];
        const newIt = [...base, item];
        const res = await saveItineraryForTrip(tripId, newIt);
        if (res.ok) {
          applyLocalEdit({ trip_id: tripId, itinerary: newIt, updatedAt: new Date().toISOString() });
          setSelectedTrip((s) => s ? { ...s, itinerary: newIt } : s);
          setItinerary(newIt);
          setToast({ msg: "Itinerary saved", type: "success" });
          setTimeout(() => setToast(null), 1400);
          return;
        } else {
          setToast({ msg: "Saved locally (failed to sync)", type: "warning" });
          setTimeout(() => setToast(null), 2200);
          setSelectedTrip((s) => s ? { ...s, itinerary: newIt } : s);
          setItinerary(newIt);
          return;
        }
      } catch (err) {
        console.error("addItinerary persist error", err);
        setToast({ msg: "Saved locally (error persisting)", type: "warning" });
        setTimeout(() => setToast(null), 2200);
      }
    } else {
      setSelectedTrip((s) => s ? { ...s, itinerary: [...(s.itinerary || []), item] } : s);
      setToast({ msg: "Added to local itinerary", type: "info" });
      setTimeout(() => setToast(null), 1500);
    }
  };

  const deleteItineraryItem = async (itemId) => {
    if (!itemId) return;
    setItinerary((prev) => (Array.isArray(prev) ? prev.filter((it) => it.id !== itemId) : prev));

    const tripId = selectedTrip?.trip_id || selectedTripId;
    const isOwnerLocal = user && (selectedTrip?.ownerId === user.uid || selectedTrip?.owner_id === user.uid);

    if (useFirebase && db && tripId && isOwnerLocal) {
      try {
        const base = Array.isArray(selectedTrip?.itinerary) ? [...selectedTrip.itinerary] : (Array.isArray(itinerary) ? [...itinerary] : []);
        const newIt = base.filter((it) => it.id !== itemId);
        const res = await saveItineraryForTrip(tripId, newIt);
        if (res.ok) {
          applyLocalEdit({ trip_id: tripId, itinerary: newIt, updatedAt: new Date().toISOString() });
          setSelectedTrip((s) => (s ? { ...s, itinerary: newIt } : s));
          setItinerary(newIt);
          setToast({ msg: "Itinerary item deleted", type: "success" });
          setTimeout(() => setToast(null), 1400);
          return;
        } else {
          setToast({ msg: "Deleted locally (failed to sync)", type: "warning" });
          setTimeout(() => setToast(null), 2200);
          setSelectedTrip((s) => (s ? { ...s, itinerary: newIt } : s));
          setItinerary(newIt);
          return;
        }
      } catch (err) {
        console.error("deleteItineraryItem persist error", err);
        setToast({ msg: "Deleted locally (error persisting)", type: "warning" });
        setTimeout(() => setToast(null), 2200);
      }
    } else {
      setSelectedTrip((s) =>
        s
          ? { ...s, itinerary: (Array.isArray(s.itinerary) ? s.itinerary.filter((it) => it.id !== itemId) : []) }
          : s
      );
      setToast({ msg: "Itinerary item removed (local only)", type: "info" });
      setTimeout(() => setToast(null), 1200);
    }
  };

  async function deleteTripConfirm(id) {
    if (!id) return;
    if (useFirebase && user) {
      try {
        await deleteDoc(doc(db, "trips", id));
      } catch (err) {
        console.error("delete trip firestore error", err);
        setSavedTrips((s) => s.filter((t) => t.trip_id !== id));
      }
    } else {
      setSavedTrips((s) => s.filter((t) => t.trip_id !== id));
    }
    if (selectedTripId === id) {
      setSelectedTripId(null);
      setSelectedTrip(null);
    }
    setConfirmDelete({ open: false, id: null });
  }

  async function handleSaveEdit(updatedFields) {
    if (!tripToEdit || !tripToEdit.trip_id) return;
    setSavingEdit(true);
    try {
      const merged = { ...tripToEdit, ...updatedFields };

      // upload media if needed (unchanged)
      if (Array.isArray(merged.media)) {
        const needUpload = merged.media.some((m) => m && m.file);
        if (needUpload && useFirebase && user) {
          merged.media = await uploadAndSaveMedia(merged.media, merged.trip_id, user);
        }
      }

      const isOwnerLocal = user && (merged.ownerId === user.uid || merged.owner_id === user.uid);
      if (useFirebase && user && isOwnerLocal) {
        try {
          const docRef = doc(db, "trips", merged.trip_id);

          // ---- normalize tags if present ----
          const rawTags = Array.isArray(merged.tags) ? merged.tags : (Array.isArray(merged.tags_list) ? merged.tags_list : []);
          const normalizedTags = (rawTags || [])
            .map((t) => (typeof t === "string" ? t.trim().toLowerCase() : ""))
            .filter(Boolean)
            .slice(0, 50); // optional safety cap

          // build payload (now includes tags + compatibility fields)
          const payload = {
            title: merged.title,
            slug: merged.slug || slugify(merged.title || "") || undefined,
            startDate: merged.startDate ?? merged.start_date ?? null,
            endDate: merged.endDate ?? merged.end_date ?? null,
            visibility: merged.visibility || (merged.private ? "private" : "public"),
            itinerary: merged.itinerary || [],
            media: merged.media || [],
            notes: merged.notes || merged.tripNotes || null,
            destination: merged.destination || merged.dest || null,
            startLocation: merged.startLocation || merged.start_location || null,
            allowedUsers:
              (merged.visibility || (merged.private ? "private" : "public")) === "restricted"
                ? (Array.isArray(merged.allowedUsers) ? merged.allowedUsers.slice() : [])
                : [],
            // Add tags into the payload (primary field)
            tags: normalizedTags,
            updatedAt: new Date().toISOString(),
          };

          // Ensure owner is always in allowedUsers for restricted visibility
          if (payload.visibility === "restricted") {
            try {
              const ownerId = merged.ownerId || merged.owner_id || user?.uid || null;
              if (ownerId) {
                const set = new Set(Array.isArray(payload.allowedUsers) ? payload.allowedUsers : []);
                set.add(ownerId);
                payload.allowedUsers = Array.from(set);
              }
            } catch (err) { /* ignore */ }
          }

          // remove undefined fields
          Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

          // debug log before save (temporary dev help)
          console.log("[Travel] saving trip payload:", merged.trip_id, payload);

          // write to Firestore — this replaces the fields included above
          await updateDoc(docRef, payload);

          // update local state with what we saved (applyLocalEdit expects trip_id + fields)
          applyLocalEdit({ trip_id: merged.trip_id, ...payload });
        } catch (err) {
          console.error("[Travel] save edit to Firestore failed:", err);
          // keep local changes even if Firestore failed
          applyLocalEdit(merged);
          setToast({ msg: "Saved locally (failed to sync)", type: "warning" });
          setTimeout(() => setToast(null), 2000);
        }
      } else {
        applyLocalEdit(merged);
      }

      setShowEditModal(false);
      setTripToEdit(null);
      setToast({ msg: "Trip updated", type: "success" });
      setTimeout(() => setToast(null), 1600);
    } finally {
      setSavingEdit(false);
    }
  }

  const openEditModal = (t) => {
    if (!t) return;
    const isOwnerLocal = user && (t.ownerId === user.uid || t.owner_id === user.uid);
    if (!isOwnerLocal) {
      setToast({ msg: "You don't have permission to edit this trip", type: "warning" });
      setTimeout(() => setToast(null), 2000);
      return;
    }
    setTripToEdit(t);
    setShowEditModal(true);
  };

  function applyLocalEdit(updated) {
    if (!updated || !updated.trip_id) return;
    if (selectedTripId === updated.trip_id) {
      const merged = { ...selectedTrip, ...updated };
      setSelectedTrip(merged);
      setTripTitle(merged.title || "");
      setTripStart(merged.startDate ?? merged.start_date ?? "");
      setTripEnd(merged.endDate ?? merged.end_date ?? "");
      setTripStartLocation(merged.startLocation || merged.start_location || "");
      setTripDestination(merged.destination || merged.dest || "");
      setTripNotes(merged.notes || "");
      if (Array.isArray(merged.itinerary)) setItinerary(merged.itinerary);
    }
    setSavedTrips((prev) => prev.map((t) => (t.trip_id === updated.trip_id ? { ...t, ...updated } : t)));
  }

  const canEditItinerary = useMemo(() => {
    return Boolean(
      !isPublicView ||
      (user && (
        (resolvedUid && user.uid === resolvedUid) ||
        (selectedTrip && (selectedTrip.ownerId === user.uid || selectedTrip.owner_id === user.uid))
      ))
    );
  }, [isPublicView, user, resolvedUid, selectedTrip]);

  // aggregate likes/views for the profile's trips (used when viewing a profile)
  const profileAggregates = useMemo(() => {
    const out = { likes: 0, views: 0 };
    if (!Array.isArray(savedTrips) || savedTrips.length === 0) return out;

    for (const t of savedTrips) {
      // tolerate multiple possible field names
      const likes =
        Number(t.likesCount ?? t.likes ?? t.likes_count ?? (Array.isArray(t.likedBy) ? t.likedBy.length : 0)) || 0;
      const views = Number(t.views ?? t.views_count ?? 0) || 0;
      out.likes += likes;
      out.views += views;
    }
    return out;
  }, [savedTrips]);

  // follow/unfollow
  async function toggleFollow() {
    if (!resolvedUid || !user || !user.uid) {
      setToast({ msg: "Sign in to follow", type: "warning" }); setTimeout(() => setToast(null), 1400);
      return;
    }
    if (resolvedUid === user.uid) {
      setToast({ msg: "You cannot follow yourself", type: "warning" }); setTimeout(() => setToast(null), 1400);
      return;
    }
    if (!useFirebase || !db) {
      setIsFollowing((p) => !p);
      return;
    }

    const followDocId = `${user.uid}_${resolvedUid}`;
    const followRef = doc(db, "follows", followDocId);

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await deleteDoc(followRef);
        setIsFollowing(false);
        setToast({ msg: "Unfollowed", type: "info" });
      } else {
        await setDoc(followRef, { followerId: user.uid, followeeId: resolvedUid, createdAt: serverTimestamp() });
        setIsFollowing(true);
        setToast({ msg: "Now following", type: "success" });
      }
    } catch (err) {
      console.error("toggleFollow (follows collection) failed:", err);
      setToast({ msg: "Failed to update follow", type: "warning" });
    } finally {
      setFollowLoading(false);
      setTimeout(() => setToast(null), 1400);
    }
  }

  function getTripStatus(trip, now = Date.now()) {
    if (!trip) return { key: "unknown", label: "Unknown", icon: "❓", color: "gray" };

    const startMs = Date.parse(readStartRaw(trip) ?? "");
    const endMs = parseDateToMsInclusive(trip.endDate ?? trip.end_date ?? "");
    const started = trip.started === true;
    const stopped = trip.stopped === true || trip.stoppedAt || trip.stopped_at;

    if (Number.isFinite(startMs) && startMs > now) {
      return { key: "upcoming", label: "Upcoming", icon: "📅", color: "blue" };
    }

    if (Number.isFinite(endMs) && endMs < now) {
      return { key: "completed", label: "Completed", icon: "✅", color: "green" };
    }

    if (started && !stopped) {
      return { key: "active", label: "Active now", icon: "🚴", color: "red" };
    }

    return { key: "ongoing", label: "Ongoing (paused)", icon: "⏸️", color: "orange" };
  }

  // loadTrip UI helpers
  const loadTrip = (t) => {
    if (!t) return;

    setSelectedTrip(t);
    if (t && t.trip_id) {
      incrementViewOnce(t.trip_id);
    }

    setSelectedTripId(t.trip_id);
    setTripTitle(t.title || "");
    setTripStart(t.startDate ?? t.start_date ?? "");
    setTripEnd(t.endDate ?? t.end_date ?? "");
    setTripPrivate(!!t.private);
    setItinerary(t.itinerary || []);
    setTripStartLocation(t.startLocation || t.start_location || "");
    setTripDestination(t.destination || t.dest || "");
    setTripNotes(t.notes || "");
    setMedia((t.media || []).map((x) => ({
      id: x.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: x.type || "image",
      url: x.storageUrl || x.url || "",
      name: x.name || "",
      date: x.date || new Date().toISOString(),
    })));
    if (t.last_position) setPosition(t.last_position);

    try {
      const slug = t.slug || (t.title ? slugify(t.title) : t.trip_id);
      const handlePart = normalizeHandle(profile?.handle || routeHandle || "");
      const basePath = handlePart ? `/@${handlePart}` : "/Travel";
      navigate(`${basePath}/${slug}`, { replace: true });
    } catch (err) {
      console.warn("[Travel] failed to update URL for trip:", err);
    }
  };

  // perform confirmed delete (for itinerary item)
  const confirmDeleteItineraryConfirmed = async () => {
    const id = confirmItin.id;
    setConfirmItin({ open: false, id: null, title: null });
    if (!id) return;
    try {
      await deleteItineraryItem(id);
    } catch (err) {
      console.error("confirmed delete failed", err);
      setToast({ msg: "Failed to delete item", type: "warning" });
      setTimeout(() => setToast(null), 2000);
    }
  };

  // loads followers (who follow current user) and returns array of {id, displayName, handle, email}
  // inside Travel component (or a nearby utils area)
  async function loadFollowersForCurrentUser() {
    if (!useFirebase || !db || !user || !user.uid) {
      setShareFollowers([]);
      return [];
    }
    setToast && setToast({ msg: "Loading followers…", type: "info" });
    try {
      const q = query(collection(db, "follows"), where("followeeId", "==", user.uid));
      const snaps = await getDocs(q);
      const followerIds = [];
      snaps.forEach((d) => {
        const data = d.data() || {};
        if (data.followerId) followerIds.push(data.followerId);
      });

      const results = await Promise.all(
        followerIds.map(async (fid) => {
          try {
            const uSnap = await getDoc(doc(db, "users", fid));
            if (uSnap && uSnap.exists()) {
              const ud = uSnap.data();
              return { id: fid, displayName: ud.displayName || ud.name || ud.handle || `user-${fid.slice(0, 6)}`, handle: ud.handle || null, email: ud.email || null, avatarUrl: ud.photoURL || ud.avatar || null };
            }
          } catch (e) { /* ignore individual failures */ }
          return { id: fid, displayName: `user-${fid.slice(0, 6)}`, handle: null, email: null };
        })
      );

      setShareFollowers(results.filter(Boolean));
      setTimeout(() => setToast && setToast(null), 700);
      return results;
    } catch (err) {
      console.error("loadFollowersForCurrentUser", err);
      setShareFollowers([]);
      setToast && setToast({ msg: "Failed to load followers", type: "warning" });
      setTimeout(() => setToast && setToast(null), 1400);
      return [];
    }
  }

  // render
  if (profileLoading) {
    return (
      <div className="travel-container" style={{ padding: 24 }}>
        <h2>Loading profile…</h2>
        <p className="muted">handle: @{normalizeHandle(routeHandle)}</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="travel-container" style={{ padding: 24 }}>
        <h2>No user found for @{normalizeHandle(routeHandle)}</h2>
        <p className="muted">Make sure the handle is correct and exists in Firestore.</p>
      </div>
    );
  }

  function openSightModal(s) {
    setModalSight(s);
    setSightModalOpen(true);
  }

  function closeSightModal() {
    setSightModalOpen(false);
    setModalSight(null);
  }

  // --- VIEW counting (client-side dedupe + server-side via your endpoint) ---
  async function incrementViewOnce(tripId) {
    if (!tripId) {
      // console.debug("incrementViewOnce: no tripId, returning");
      return;
    }

    const key = `jift_viewed_${tripId}`;
    const now = Date.now();
    const RECOUNT_AFTER_MS = 1000 * 60 * 60 * 24; // 24h - adjust as needed

    // console.debug("incrementViewOnce: start", { tripId, key, now, useFirebase });

    // --- Check last seen timestamp and skip if within window ---
    let lastSeenRaw = null;
    try { lastSeenRaw = localStorage.getItem(key); } catch (e) { console.warn("incrementViewOnce: localStorage read failed", e); }
    if (lastSeenRaw) {
      const lastTs = Number(lastSeenRaw);
      if (Number.isFinite(lastTs) && (now - lastTs) < RECOUNT_AFTER_MS) {
        // console.debug("incrementViewOnce: skipping due to dedupe window", { lastTs, ageMs: now - lastTs });
        return;
      }
    }

    // Mark now optimistically
    try { localStorage.setItem(key, String(now)); } catch (e) { console.warn("incrementViewOnce: localStorage set failed", e); }

    // --- Force the fetch for debugging (bypass any useFirebase guard) ---
    const fnUrl = "https://incrementview-nuc3fsjg5q-uc.a.run.app";
    // console.debug("incrementViewOnce: will fetch", fnUrl);

    // Timeout / abort helper
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      try { controller.abort(); } catch (e) { /* ignore */ }
    }, 6000);

    try {
      const resp = await fetch(fnUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tripId }),
        signal: controller.signal,
        // we do not add credentials; your function handles CORS
      });

      clearTimeout(timeout);

      // console.debug("incrementViewOnce: fetch returned", resp.status, resp.statusText);

      let json = null;
      try { json = await resp.json(); } catch (e) { console.warn("incrementViewOnce: response JSON parse failed", e); }

      // console.debug("incrementViewOnce: response JSON", json);

      if (!resp.ok) {
        // console.warn("incrementViewOnce: server responded non-OK", { status: resp.status, json });
        // optional: remove mark to allow retry on next page load
        // localStorage.removeItem(key);
      } else {
        console.info("incrementViewOnce: success", { tripId, views: json && json.views });
      }
    } catch (err) {
      clearTimeout(timeout);
      if (err.name === "AbortError") {
        // console.error("incrementViewOnce: fetch aborted (timeout)", err);
      } else {
        // console.error("incrementViewOnce: fetch failed", err);
      }

      // Undo optimistic mark so developer can retry quickly
      try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
    }
  }
  const likedByMe = Boolean(
    selectedTrip && Array.isArray(selectedTrip.likedBy) && user && selectedTrip.likedBy.includes(user.uid)
  );

  async function handleToggleLike(tripId) {
    if (!tripId) return;
    if (!user || !user.uid) {
      setToast({ msg: "Sign in to like trips", type: "warning" }); setTimeout(() => setToast(null), 1400);
      return;
    }

    // derive current states
    const wasLiked = Boolean(selectedTrip && Array.isArray(selectedTrip.likedBy) && selectedTrip.likedBy.includes(user.uid));
    const currentLikes = Number(selectedTrip?.likesCount ?? selectedTrip?.likes ?? 0);

    // optimistic update: update selectedTrip and savedTrips
    const newLikesCount = wasLiked ? Math.max(0, currentLikes - 1) : currentLikes + 1;
    const newLikedBy = wasLiked
      ? (selectedTrip.likedBy || []).filter((id) => id !== user.uid)
      : [...(selectedTrip.likedBy || []), user.uid];

    const optimistic = { trip_id: tripId, likesCount: newLikesCount, likedBy: newLikedBy };
    applyLocalEdit(optimistic);
    setSelectedTrip((s) => (s ? { ...s, ...optimistic } : s));

    try {
      // call the helper you moved to tripHelpers (it returns server data if successful)
      const data = await toggleLike(tripId, auth); // toggleLike(tripId, auth) -> returns { ok, liked, likesCount, tripId } ideally
      // If server returned counts, apply them; otherwise rely on optimistic
      if (data && (data.likesCount !== undefined || data.likes !== undefined)) {
        const serverCount = Number(data.likesCount ?? data.likes ?? newLikesCount);
        const serverLikedBy = data.likedBy ?? (data.liked ? newLikedBy : (wasLiked ? newLikedBy : newLikedBy));
        applyLocalEdit({ trip_id: tripId, likesCount: serverCount, likedBy: serverLikedBy });
        setSelectedTrip((s) => (s ? { ...s, likesCount: serverCount, likedBy: serverLikedBy } : s));
      }
      setToast({ msg: data && data.liked ? "Liked" : "Like updated", type: "success" });
    } catch (err) {
      // rollback optimistic change on failure
      applyLocalEdit({ trip_id: tripId, likesCount: currentLikes, likedBy: (selectedTrip?.likedBy || []) });
      setSelectedTrip((s) => (s ? { ...s, likesCount: currentLikes, likedBy: (selectedTrip?.likedBy || []) } : s));
      console.error("toggleLike failed", err);
      setToast({ msg: "Failed to update like", type: "warning" });
    } finally {
      setTimeout(() => setToast(null), 1400);
    }
  }

  // Use explicit region (match where toggleLike is deployed)
  // Replace "us-central1" if you deployed to a different region.
  const functions = getFunctions(firebaseApp, "us-central1");
  const callToggleLike = httpsCallable(functions, "toggleLike");

  // inside Travel.js (where you call toggleLike)
  async function toggleLike(tripId) {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setToast({ msg: "Sign in to like trips", type: "warning" });
      setTimeout(() => setToast(null), 1400);
      throw new Error("no-auth");
    }

    const idToken = await currentUser.getIdToken(true);
    const fnUrl =
      "https://us-central1-travel-6c761.cloudfunctions.net/toggleLike";

    try {
      const resp = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ tripId }),
      });

      const data = await resp.json();
      if (!resp.ok) {
        // data.error may contain server message
        const errMsg = data && data.error ? data.error : resp.statusText;
        setToast({ msg: errMsg, type: "warning" });
        setTimeout(() => setToast(null), 1400);
        throw new Error(errMsg);
      }

      // returns {ok:true, liked, likesCount, tripId}
      return data;
    } catch (err) {
      console.error("toggleLike failed", err);
      setToast({ msg: "Failed to toggle like", type: "warning" });
      setTimeout(() => setToast(null), 1400);
      throw err;
    }
  }

  return (
    <div className="travel-container">
      <TripNotificationBanner
        selectedTrip={selectedTrip}
        user={user}
        isOwner={isOwner}
        tripStart={tripStart}
        tripEnd={tripEnd}
        statusKey={getTripStatus(selectedTrip).key}
        onStop={stopTripNow}
        onExtend={extendEndBy24Hours}
      />

      <header className="travel-header" style={{ position: "relative" }}>
        <div style={{ position: "relative", width: "100%" }}>
          <div style={{ minWidth: 0, width: "100%" }}>
            <h1 style={{ margin: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              {/* LEFT: flexible area containing the text that SHOULD truncate — KEEP subtitle and other header content unchanged */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                {/* Title (keeps your original styling and truncation behavior) */}
                <span
                  className="trip-title"
                  style={{
                    flex: 1,
                    minWidth: 0,           // important for child ellipsis to work
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    display: "inline-block",
                  }}
                >
                  {selectedTripId
                    ? (tripTitle || "Trip Title")
                    : (isPublicView
                      ? (profile?.displayName || "Profile")
                      : (tripTitle ? `${tripTitle} — Live` : "Untitled Trip — Live"))}
                </span>

                {/* GROUP: visibility, like, views, live — pinned to left, never ellipsized */}
                <div
                  className="trip-title-badges"
                  style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 6 }}
                  aria-hidden="false"
                >
                  {/* Visibility Icon */}
                  {selectedTrip && (
                    <span
                      title={getVisibilityInfo(selectedTrip, user).label}
                      style={{ fontSize: 16, opacity: 0.95, flexShrink: 0, display: "inline-flex", alignItems: "center" }}
                    >
                      {getVisibilityInfo(selectedTrip, user).icon}
                    </span>
                  )}

                  {/* Like button + count (use existing toggleLike) */}
                  {isPublicView && !selectedTripId ? (
                    // PROFILE VIEW (no trip selected): show aggregated totals (non-clickable)
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 6 }}>
                      <div
                        className="trip-count-badge like-badge"
                        title={`${profileAggregates.likes} total likes`}
                        style={{ padding: "4px 6px", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, flexShrink: 0 }}
                        aria-label={`Total likes for profile: ${profileAggregates.likes}`}
                      >
                        <span className="heart" aria-hidden="true" style={{ fontSize: 15 }}>♥</span>
                        <span>{profileAggregates.likes}</span>
                      </div>

                      <div
                        className="trip-count-badge views-badge"
                        title={`${profileAggregates.views} total views`}
                        style={{ padding: "4px 6px", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--muted)", flexShrink: 0 }}
                        aria-label={`Total views for profile: ${profileAggregates.views}`}
                      >
                        <span style={{ fontSize: 15 }}>👁️</span>
                        <span>{profileAggregates.views}</span>
                      </div>
                    </div>
                  ) : (
                    // Trip view (or local/dashboard): show per-trip counts and like button (clickable)
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, marginLeft: 6 }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (!selectedTrip || !selectedTrip.trip_id) return; handleToggleLike(selectedTrip.trip_id); }}
                        className={`trip-count-badge like-badge ${likedByMe ? "liked" : ""}`}
                        title={`${selectedTrip?.likesCount ?? 0} likes`}
                        style={{ border: "none", background: "transparent", padding: "4px 6px", display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", flexShrink: 0 }}
                        aria-label={`Like (${selectedTrip?.likesCount ?? 0})`}
                      >
                        <span className="heart" aria-hidden="true" style={{ fontSize: 15, lineHeight: 1 }}>
                          {likedByMe ? "♥" : "♡"}
                        </span>
                        <span style={{ fontSize: 13 }}>{selectedTrip?.likesCount ?? 0}</span>
                      </button>

                      <div
                        className="trip-count-badge views-badge"
                        title={`${selectedTrip?.views ?? 0} views`}
                        style={{ padding: "4px 6px", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--muted)", flexShrink: 0 }}
                        aria-label={`Views: ${selectedTrip?.views ?? 0}`}
                      >
                        <span style={{ fontSize: 15 }}>👁️</span>
                        <span>{selectedTrip?.views ?? 0}</span>
                      </div>
                    </div>
                  )}

                  {/* LIVE badge (keeps at left group) */}
                  {isTripActive(selectedTrip) && (
                    <span
                      className="live-badge"
                      style={{
                        marginLeft: 2,
                        backgroundColor: "#ef4444",
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "bold",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    >
                      LIVE
                    </span>
                  )}
                </div>
              </div>

              {/* RIGHT: actions (never should be overlapped) */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {isPublicView && resolvedUid && user && resolvedUid !== user.uid && (
                  <button
                    onClick={(e) => { e.stopPropagation(); if (followLoading) return; toggleFollow(); }}
                    className={`btn-text-follow ${isFollowing ? "following" : ""}`}
                    title={isFollowing ? "Unfollow" : "Follow"}
                    aria-pressed={isFollowing}
                    style={{ background: "none", border: "none", padding: 0 }}
                  >
                    {followLoading ? "..." : (isFollowing ? "Following" : "Follow")}
                  </button>
                )}

                {isPublicView && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (user && user.uid) await loadFollowersForCurrentUser();
                      else setShareFollowers([]);
                      const sharingTrip = Boolean(selectedTripId && selectedTrip);
                      const handleStr = profile?.handle || normalizeHandle(routeHandle) || "";
                      const tripNameStr = sharingTrip ? (selectedTrip?.title || tripTitle || "") : "";
                      setShareModalContext({ isProfile: !sharingTrip, handle: handleStr, tripName: tripNameStr });
                      setShowShareModal(true);
                    }}
                    aria-label="Share profile/trip"
                    className="btn-text-follow"
                  >
                    Share
                  </button>
                )}
                {/* Edit button shown only to owner */}
                {selectedTrip && user && (selectedTrip.ownerId === user.uid || selectedTrip.owner_id === user.uid) && (
                  <button
                    onClick={() => openEditModal(selectedTrip)}
                    className="btn-secondary edit-btn"
                    title="Edit this trip"
                  >
                    Edit Trip
                  </button>
                )}

                {/* Message button: visible to everyone except the owner of the trip or their own profile */}
                {profile && (!user || (
                  user.uid !== profile.uid &&            // not viewing your own profile
                  (!selectedTrip || (selectedTrip.ownerId !== user.uid && selectedTrip.owner_id !== user.uid)) // not your own trip
                )) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowMessageModal(true); }}
                      className="btn-secondary"
                      title={`Send a message to @${profile?.handle || ""}`}
                      style={{ marginLeft: 6 }}
                    >
                      Message
                    </button>
                  )}



              </div>
            </h1>

            {/* put display name / handle & followers as subtitle (so it won't be ellipsized by the title row) */}
            {isPublicView && (
              <div
                className="subtitle"
                style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}
              >
                <a
                  href={`/@${normalizeHandle(profile?.handle ?? profile?.displayName ?? "")}`}
                  style={{
                    color: "inherit",
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontWeight: 600,
                  }}
                >
                  {/* Both parts are inside the link */}
                  <span style={{ whiteSpace: "nowrap" }}>
                    {profile?.displayName ?? "Profile"}
                  </span>
                  <span>@{profile?.handle ?? profile?.displayName}</span>
                </a>

                <span>·</span>
                <span>{followerCount} followers</span>
              </div>
            )}


            {/* Trip meta / notes unchanged... */}
            {selectedTripId ? (
              <>
                <div className="muted meta-line" style={{ marginTop: 6 }}>
                  {tripStartLocation || tripDestination ? ` ${tripStartLocation || "—"} → ${tripDestination || "—"}` : ""}
                  {(tripStart || tripEnd) ? ` · ${tripStart}${tripEnd ? ` → ${tripEnd}` : ""}` : ""}
                </div>

                {tripNotes && (
                  <div className="muted trip-notes" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                    {tripNotes}
                  </div>
                )}

                {selectedTrip && Array.isArray(selectedTrip.tags) && selectedTrip.tags.length > 0 && (
                  <div className="tags-row" style={{ marginTop: 8, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, fontSize: 13 }}>
                    <span className="muted" style={{ fontSize: 12 }}>Tags:</span>
                    {selectedTrip.tags.map((t, i) => (
                      <span key={`tag-${i}`} className="trip-tag-badge" style={{ fontSize: 12 }} title={`#${t}`}>
                        #{t}
                      </span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              isPublicView ? (
                <div className="muted" style={{ fontSize: 13 }}>{profile?.bio}</div>
              ) : (
                <>
                  <div className="muted" style={{ fontSize: 13 }}>{tripStart} {tripEnd ? `→ ${tripEnd}` : ""}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {useFirebase ? (firebaseReady ? "Connected to Firebase" : "Connecting...") : "Local-only mode (no Firebase configured)"}
                  </div>
                </>
              )
            )}
          </div>
        </div>
      </header>

      <div className="layout-grid">
        <aside className="layout-sidebar">

          <TripsList
            savedTrips={savedTrips}
            selectedTripId={selectedTripId}
            loadTrip={loadTrip}
            user={user}
            isPublicView={isPublicView}
            openEditModal={openEditModal}
            setConfirmDelete={setConfirmDelete}
            // show owner actions only when this is the owner's dashboard (explicitly)
            showOwnerActions={Boolean(!isPublicView && user && resolvedUid && user.uid === resolvedUid)}
          />
        </aside>

        {(!isPublicView || Boolean(selectedTripId)) ? (
          <main>
            {(() => {
              const showLive = isTripActive(selectedTrip); // true when trip started/live
              const gridColumns = showLive ? "1fr 340px" : "1fr";

              return (
                <>
                  <div
                    className={`map-right-grid ${!showLive ? "map-right-grid--solo" : ""}`}
                    style={{ gridTemplateColumns: gridColumns }}
                  >
                    <div className="map-column">
                      <MapPanel
                        selectedTrip={selectedTrip}
                        setShowEmbedModal={setShowEmbedModal}
                        setGoogleEmbedInput={setGoogleEmbedInput}
                        isPublicView={isPublicView}
                        user={user}
                        setToast={setToast}
                      />
                    </div>
                    {showLive && (
                      <aside className="right-panel">
                        <div className="side-top">
                          <div className="section right-card">
                            {/* header row (optional) */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                              <h3 style={{ margin: 0, fontSize: 16 }}>Live location</h3>
                              <span style={{ fontSize: 12, color: "var(--muted)" }}>{isTripActive(selectedTrip) ? "Live" : "—"}</span>
                            </div>

                            {/* LiveLocationPanel should fill the card area */}
                            <div style={{ width: "100%", minHeight: 140 }}>
                              <LiveLocationPanel
                                db={db}
                                selectedTrip={selectedTrip}
                                currentUserId={user?.uid}
                                compact={true}
                                mapHeight="180px"
                                mapZoom={16}
                              />
                            </div>
                          </div>
                        </div>
                      </aside>
                    )}

                  </div>

                  {/* ---------- BELOW: full-width content (not inside the grid) ---------- */}
                  <div style={{ width: "100%", marginTop: 18 }}>
                    {/* Live video: only shown when active but spans full width */}
                    <div style={{ width: "100%", minWidth: 0 }}>
                      {isTripActive(selectedTrip) && (
                        <LiveVideoPanel
                          selectedTrip={selectedTrip}
                          isOwner={isOwner}
                          applyLocalEdit={applyLocalEdit}
                          user={user}
                          db={db}
                          useFirebase={useFirebase}
                          setToast={setToast}
                        />
                      )}
                    </div>

                    {/* Itinerary, Sightseeing and Gallery — full width */}
                    <div style={{ display: "flex", gap: 20, marginTop: 18, flexWrap: "wrap" }}>
                      <div className="responsive-side" style={{ flex: 1 }}>
                        <div className="side-column">
                          <div className="side-panel-wrapper">
                            <SightseeingPanel
                              db={db}
                              selectedTrip={selectedTrip}
                              user={user}
                              isPublicView={isPublicView}
                              setToast={setToast}
                              savedSights={savedSights}
                            />
                          </div>

                          <div className="sights-list-wrapper">
                            {savedSights.length === 0 ? (
                              <div className="muted" style={{ fontSize: 13 }}>
                                No saved sights for this trip.
                              </div>
                            ) : (
                              <div className="sightseeing-panel">
                                {savedSights.map((s) => {
                                  const key = `${s.provider}:${s.placeId}:${s.id}`;
                                  const mode = (s.mode || s.status || s.visitMode || "").toString().toLowerCase();
                                  const modeLabel = mode ? (mode.charAt(0).toUpperCase() + mode.slice(1)) : "—";

                                  return (
                                    <div
                                      key={key}
                                      className="sight-card"
                                      style={{ cursor: "pointer" }}
                                      onClick={() => openSightModal(s)}
                                      role="button"
                                      tabIndex={0}
                                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openSightModal(s); }}
                                      aria-label={`Open details for ${s.name}`}
                                    >
                                      <div className="sight-thumb">
                                        {s.photoUrl ? <img src={s.photoUrl} alt={s.name} /> : <div className="sight-placeholder">No photo</div>}
                                      </div>

                                      <div className="sight-info">
                                        <div className="sight-name" title={s.name}>{s.name}</div>
                                        <div className="sight-meta">
                                          {s.category || ""}
                                          {s.location?.address ? ` · ${s.location.address}` : ""}
                                        </div>
                                      </div>

                                      <div className="sight-actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div className={`mode-badge mode-${mode || "default"}`} title={modeLabel} style={{ pointerEvents: "none" }}>
                                          {modeLabel}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="main-column">
                          <ItineraryPanel
                            itinerary={itinerary}
                            canEditItinerary={canEditItinerary}
                            setShowItineraryModal={setShowItineraryModal}
                            confirmDeleteItinerary={confirmDeleteItinerary}
                          />
                        </div>
                      </div>

                      <GalleryPanel
                        media={media}
                        setMedia={setMedia}
                        isPublicView={isPublicView}
                        selectedTrip={selectedTrip}
                        user={user}
                      />
                    </div>


                  </div>
                </>
              );
            })()}
          </main>

        ) : (
          <div className="section" style={{ minHeight: 380, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", maxWidth: 460 }}>
              <h2 style={{ marginBottom: 10, fontSize: "1.5rem", fontWeight: 600 }}>{isPublicView ? "Select a trip to view" : "No trip selected"}</h2>
              <p className="muted" style={{ marginBottom: 20, fontSize: 15, lineHeight: 1.5, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>
                {isPublicView ? "This profile has trips on the left. Pick one to load its map, live stream, gallery and itinerary." : "Pick a trip from the list on the left or create a new trip to begin tracking and broadcasting."}
              </p>

              {!isPublicView && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button className="btn-start" onClick={() => createLocalTrip({ title: "My New Trip" })}>Create new trip</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showItineraryModal && (
        <Modal title={`Add Itinerary — ${selectedTrip?.title || tripTitle || "Untitled Trip"}`} onClose={() => setShowItineraryModal(false)}>
          <AddItineraryForm tripTitle={selectedTrip?.title || tripTitle || "Untitled Trip"} onAdd={(item) => { addItinerary(item); setShowItineraryModal(false); }} onCancel={() => setShowItineraryModal(false)} />
        </Modal>
      )}

      {showEditModal && tripToEdit && (
        <EditTripModal
          trip={tripToEdit}
          open={showEditModal}
          saving={savingEdit}
          onCancel={() => { setShowEditModal(false); setTripToEdit(null); }}
          onSave={handleSaveEdit}
          db={db}
          ownerUid={user?.uid || null}
          resolveHandleToUid={resolveHandleToUidLocal}
          resolveUidToHandle={resolveUidToHandleLocal}
        />
      )}

      {showEmbedModal && (
        <Modal title={`Add Google Map — ${selectedTrip?.title || "Trip"}`} onClose={() => setShowEmbedModal(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 320 }}>
            <div style={{ fontSize: 13, color: "#555" }}>Paste the iframe HTML or the embed URL from Google My Maps / Google Maps:</div>
            <input placeholder={`Paste iframe or src URL (e.g. <iframe src="..."> or https://www.google.com/maps/d/.../embed)`} value={googleEmbedInput} onChange={(e) => setGoogleEmbedInput(e.target.value)} style={{ width: "100%", padding: "8px 10px", fontSize: 14 }} />

            {extractEmbedSrc(googleEmbedInput) ? (
              <div style={{ border: "1px solid #eee", borderRadius: 6, overflow: "hidden", height: 200 }}>
                <iframe title="Map preview" src={extractEmbedSrc(googleEmbedInput)} width="100%" height="100%" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
            ) : (
              <div className="muted" style={{ fontSize: 13 }}>Paste a Google My Maps embed iframe or a Google Maps embed URL to see a preview here.</div>
            )}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn-cancel" onClick={() => setShowEmbedModal(false)}>Cancel</button>
              <button className="btn-start" onClick={async () => { await saveGoogleEmbedForSelectedTrip(); setShowEmbedModal(false); }}>Save map</button>
            </div>
          </div>
        </Modal>
      )}

      {confirmDelete.open && (
        <Modal title="Delete Trip?" onClose={() => setConfirmDelete({ open: false, id: null })}>
          <p>Delete this trip permanently from storage?</p>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn-danger" onClick={() => deleteTripConfirm(confirmDelete.id)}>Delete</button>
            <button className="btn-cancel" onClick={() => setConfirmDelete({ open: false, id: null })}>Cancel</button>
          </div>
        </Modal>
      )}

      {confirmItin.open && (
        <Modal title="Delete itinerary item?" onClose={() => setConfirmItin({ open: false, id: null, title: null })}>
          <p>Are you sure you want to delete <strong>{confirmItin.title || "this item"}</strong> from the itinerary? This action cannot be undone.</p>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn-danger" onClick={confirmDeleteItineraryConfirmed}>Delete</button>
            <button className="btn-cancel" onClick={() => setConfirmItin({ open: false, id: null, title: null })}>Cancel</button>
          </div>
        </Modal>
      )}

      <MessageModal
        open={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        toUid={resolvedUid}
        toHandle={profile?.handle}
        db={db}
        user={user}
        useFirebase={useFirebase}
        setToast={setToast}
      />

      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        url={window.location.href}
        // we'll compute a friendly message and pass it as title/text for copy/share fallback
        title={shareModalContext.isProfile ? `@${shareModalContext.handle || (profile?.handle || "")}` : (shareModalContext.tripName || tripTitle)}
        text={shareModalContext.isProfile ? `Check out @${shareModalContext.handle}` : `Check out \"${shareModalContext.tripName}\"`}
        followers={shareFollowers}
        initialSelected={[]}
        onShareWithFollowers={async (selectedFollowerIds) => {
          // app-specific logic: create notification docs, send push, etc.
          if (!useFirebase || !db || !user) {
            setToast && setToast({ msg: "Shared locally (no Firebase)", type: "info" });
            setTimeout(() => setToast && setToast(null), 1400);
            return;
          }

          try {
            // Resolve actor handle (try helper, fallback to displayName or short uid)
            let actorHandle = null;
            try {
              actorHandle = (typeof resolveUidToHandleLocal === "function") ? await resolveUidToHandleLocal(user.uid) : null;
            } catch (e) {
              actorHandle = null;
            }
            let actor = actorHandle ? `@${actorHandle}` : (user.displayName ? `@${normalizeHandle(user.displayName)}` : `@${(user.uid || "").slice(0, 6)}`);

            // Build the exact message formats you wanted.
            let notifyMessage = "";
            if (shareModalContext.isProfile) {
              const targetHandle = shareModalContext.handle ? `@${shareModalContext.handle}` : (profile?.handle ? `@${profile.handle}` : "");
              notifyMessage = `${actor} share ${targetHandle} profile with you`;
            } else {
              const tripName = shareModalContext.tripName || tripTitle || "this trip";
              notifyMessage = `${actor} share trip "${tripName}" with you`;
            }

            // Persist notifications for each selected follower
            await Promise.all(
              selectedFollowerIds.map(async (fid) => {
                const nref = doc(collection(db, "notifications"));
                await setDoc(nref, {
                  to: fid,
                  from: user.uid || null,
                  url: window.location.href,
                  title: notifyMessage,   // put the constructed message as the notification title
                  text: notifyMessage,    // optional — keep text same as title for simplicity
                  type: "share",
                  createdAt: serverTimestamp(),
                  read: false,
                });
              })
            );

            setToast && setToast({ msg: `Shared with ${selectedFollowerIds.length} follower(s)`, type: "success" });
            setTimeout(() => setToast && setToast(null), 1400);
          } catch (err) {
            console.error("onShareWithFollowers:", err);
            setToast && setToast({ msg: "Failed to share with followers", type: "warning" });
            setTimeout(() => setToast && setToast(null), 1600);
          }
        }}
        db={db}
        currentUserId={user?.uid}
      />


      <InspectSightModal
        open={sightModalOpen}
        onClose={closeSightModal}
        sight={modalSight}
        db={db}
        selectedTrip={selectedTrip}
        user={user}
        setToast={setToast}
        onSaved={(updatedSight) => {
          // update local savedSights snapshot quickly
          setSavedSights((prev) => prev.map((x) => (x.id === updatedSight.id ? { ...x, ...updatedSight } : x)));
        }}
        onDeleted={(deletedId) => {
          setSavedSights((prev) => prev.filter((x) => x.id !== deletedId));
        }}
      />

    </div>
  );
}

// helper used in JSX but declared earlier
function getVisibilityInfo(trip, user) {
  if (!trip) return { icon: "❓", label: "Unknown" };
  const visibility = trip.visibility || (trip.private ? "private" : "public");
  const isOwnerLocal = user && (trip.ownerId === user.uid || trip.owner_id === user.uid);

  if (visibility === "public") {
    return { icon: "🌐", label: "Public" };
  }

  if (visibility === "restricted") {
    if (isOwnerLocal) return { icon: "🔑", label: "Restricted (you own this)" };
    if (Array.isArray(trip.allowedUsers) && user && trip.allowedUsers.includes(user.uid)) {
      return { icon: "🔑", label: "Restricted (you have access)" };
    }
    return { icon: "🔑", label: "Restricted" };
  }

  if (visibility === "private") {
    if (isOwnerLocal) return { icon: "🔒", label: "Private (you own this)" };
    if (Array.isArray(trip.allowedUsers) && user && trip.allowedUsers.includes(user.uid)) {
      return { icon: "🔑", label: "Restricted (you have access)" };
    }
    return { icon: "🔒", label: "Private" };
  }

  return { icon: "❓", label: "Unknown" };
}
